import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import {
  markPartsAsCancelled,
  processStreamEvent,
} from "~/components/assistant/lib/messageProcessor";
import type { Message } from "~/components/assistant/types";
import { runStream, type StreamController } from "~/contexts/streamManager";
import { trackEvent } from "~/utils/monitoring";

interface ConversationState {
  conversationId: string;
  datasetId: string;
  messages: Message[];
  isStreaming: boolean;
  optimisticMessageIds: Set<string>; // Track messages not yet confirmed by server
  lastAccessedAt: number; // Timestamp for LRU cache management
}

interface ConversationsContextValue {
  getMessages: (conversationId: string) => Message[];
  getOptimisticMessageIds: (conversationId: string) => Set<string>;
  setMessages: (
    conversationId: string,
    messages: Message[],
    options?: { optimistic?: boolean }
  ) => void;
  startStream: (
    api: AssistantApi,
    conversationId: string,
    datasetId: string,
    prompt: string,
    servers: string[],
    debugMode: boolean,
    nextMessageId: () => string,
    onConversationNameUpdate?: (conversationId: string, name: string) => void
  ) => Promise<void>;
  cancelStream: (api: AssistantApi, conversationId: string) => Promise<void>;
  isStreaming: (conversationId: string) => boolean;
  markMessagesAsConfirmed: (conversationId: string, messageIds: string[]) => void;
}

const ConversationsContext = createContext<ConversationsContextValue | null>(null);

/** Maximum conversations to keep in memory (LRU eviction for older ones) */
const MAX_CACHED_CONVERSATIONS = 10;

/**
 * ConversationsProvider manages conversation state across the application.
 *
 * Architecture:
 * - Uses useState for conversation data (standard React pattern)
 * - Supports optimistic updates: messages shown immediately before server confirmation
 * - Manages active streams with cancellation capabilities
 *
 * Note: Streaming loops use local variables to track messages during async iteration,
 * avoiding stale closure issues that can occur with React state.
 */
export function ConversationsProvider({ children }: { children: ReactNode }) {
  const [conversations, setConversations] = useState<Map<string, ConversationState>>(
    () => new Map()
  );
  const [activeStreams, setActiveStreams] = useState<Map<string, StreamController>>(
    () => new Map()
  );

  // Ref to track active streams for cleanup (avoids stale closure in useEffect)
  const activeStreamsRef = useRef<Map<string, StreamController>>(activeStreams);
  activeStreamsRef.current = activeStreams;

  // Cleanup: abort all active streams when provider unmounts
  useEffect(() => {
    return () => {
      activeStreamsRef.current.forEach((controller) => {
        controller.abortController.abort();
      });
    };
  }, []);

  /** Update conversations state immutably */
  const updateConversation = useCallback(
    (
      conversationId: string,
      updater: (existing: ConversationState | undefined) => ConversationState
    ) => {
      setConversations((prev) => {
        const next = new Map(prev);
        next.set(conversationId, updater(prev.get(conversationId)));

        // LRU cleanup: evict oldest conversations if over limit
        if (next.size > MAX_CACHED_CONVERSATIONS) {
          const sorted = Array.from(next.entries()).sort(
            ([, a], [, b]) => a.lastAccessedAt - b.lastAccessedAt
          );
          for (const [id, state] of sorted) {
            if (next.size <= MAX_CACHED_CONVERSATIONS) break;
            if (!state.isStreaming) {
              next.delete(id);
            }
          }
        }

        return next;
      });
    },
    []
  );

  const getMessages = useCallback(
    (conversationId: string): Message[] => {
      return conversations.get(conversationId)?.messages ?? [];
    },
    [conversations]
  );

  const getOptimisticMessageIds = useCallback(
    (conversationId: string): Set<string> => {
      return conversations.get(conversationId)?.optimisticMessageIds ?? new Set();
    },
    [conversations]
  );

  const setMessages = useCallback(
    (conversationId: string, messages: Message[], options?: { optimistic?: boolean }) => {
      updateConversation(conversationId, (existing) => {
        const optimisticIds = existing?.optimisticMessageIds || new Set<string>();
        if (options?.optimistic) {
          messages.forEach((msg) => optimisticIds.add(msg.id));
        }
        return {
          conversationId,
          datasetId: existing?.datasetId ?? "",
          messages,
          isStreaming: existing?.isStreaming ?? false,
          optimisticMessageIds: optimisticIds,
          lastAccessedAt: Date.now(),
        };
      });
    },
    [updateConversation]
  );

  const isStreaming = useCallback(
    (conversationId: string): boolean => {
      return conversations.get(conversationId)?.isStreaming ?? false;
    },
    [conversations]
  );

  const markMessagesAsConfirmed = useCallback(
    (conversationId: string, messageIds: string[]) => {
      updateConversation(conversationId, (existing) => {
        if (!existing) {
          return {
            conversationId,
            datasetId: "",
            messages: [],
            isStreaming: false,
            optimisticMessageIds: new Set(),
            lastAccessedAt: Date.now(),
          };
        }
        const updatedOptimisticIds = new Set(existing.optimisticMessageIds);
        messageIds.forEach((id) => updatedOptimisticIds.delete(id));
        return { ...existing, optimisticMessageIds: updatedOptimisticIds };
      });
    },
    [updateConversation]
  );

  const startStream = useCallback(
    async (
      api: AssistantApi,
      conversationId: string,
      datasetId: string,
      prompt: string,
      servers: string[],
      debugMode: boolean,
      nextMessageId: () => string,
      onConversationNameUpdate?: (conversationId: string, name: string) => void
    ) => {
      const abortController = new AbortController();
      const controller: StreamController = {
        conversationId,
        abortController,
        cancelRequested: false,
      };

      setActiveStreams((prev) => new Map(prev).set(conversationId, controller));

      // Track response time
      const startTime = Date.now();

      // Mark as streaming
      updateConversation(conversationId, (existing) => ({
        conversationId,
        datasetId,
        messages: existing?.messages ?? [],
        isStreaming: true,
        optimisticMessageIds: existing?.optimisticMessageIds ?? new Set(),
        lastAccessedAt: Date.now(),
      }));

      // Track messages locally during streaming to avoid stale closure issues.
      // This is the key pattern that makes useState safe for async loops.
      let localMessages: Message[] = [];

      try {
        // Create optimistic messages
        const optimisticUserMessage: Message = {
          id: nextMessageId(),
          content: prompt,
          kind: "user_chat",
          timestamp: new Date(),
        };

        const optimisticAgentMessage: Message = {
          id: nextMessageId(),
          content: "",
          kind: "model_chat",
          timestamp: new Date(),
          parts: [],
          streaming: true,
        };

        // Get current messages and add optimistic ones
        const currentMessages = getMessages(conversationId);
        localMessages = [...currentMessages, optimisticUserMessage, optimisticAgentMessage];
        setMessages(conversationId, localMessages, { optimistic: true });

        // Process stream events
        for await (const event of runStream(
          conversationId,
          api,
          { prompt, servers, contextId: datasetId },
          abortController.signal
        )) {
          const result = processStreamEvent(localMessages, event, { nextMessageId, debugMode });
          if (result) {
            localMessages = result.messages;
            setMessages(conversationId, result.messages);

            // Handle auto-generated conversation name
            if (result.conversationName && onConversationNameUpdate) {
              onConversationNameUpdate(
                result.conversationName.conversationId,
                result.conversationName.displayName
              );
            }
          }
        }

        // Check if stream ended due to cancel request
        // cancelRequested is set immediately when user clicks cancel
        if (controller.cancelRequested) {
          // Mark as cancelled (utility handles tool calls and adds cancelled part if needed)
          localMessages = localMessages.map((msg) => {
            if (msg.kind === "model_chat" && msg.streaming) {
              return {
                ...msg,
                streaming: false,
                cancelled: true,
                parts: markPartsAsCancelled(msg.parts || []),
              };
            }
            return msg;
          });
          setMessages(conversationId, localMessages);
        } else {
          // Normal completion - mark streaming complete
          localMessages = localMessages.map((msg) =>
            msg.id === optimisticAgentMessage.id ? { ...msg, streaming: false } : msg
          );
          setMessages(conversationId, localMessages);

          // Track message received
          const completedMessage = localMessages.find((m) => m.id === optimisticAgentMessage.id);
          if (completedMessage && datasetId) {
            const artifactTypes =
              completedMessage.parts
                ?.filter((p) => ["figure", "dataframe", "value"].includes(p.type))
                .map((p) => p.type) || [];

            const toolCalls =
              completedMessage.parts
                ?.filter((p) => p.type === "toolCall")
                .map((p) => p.toolCall?.toolName)
                .filter(Boolean) || [];

            trackEvent("analysis_agent_message_received", {
              dataset_id: datasetId,
              conversation_id: conversationId,
              message_id: completedMessage.id,
              context: "dataset",
              response_time_ms: Date.now() - startTime,
              has_artifacts: artifactTypes.length > 0,
              artifact_types: artifactTypes,
              tool_calls_count: toolCalls.length,
              tools_used: toolCalls,
              trace_id: completedMessage.traceId,
            });
          }

          // Mark optimistic messages as confirmed
          markMessagesAsConfirmed(conversationId, [
            optimisticUserMessage.id,
            optimisticAgentMessage.id,
          ]);
        }
      } catch (error) {
        console.error("Failed to stream message:", error);

        // Track error
        if (datasetId) {
          trackEvent("analysis_agent_message_error", {
            dataset_id: datasetId,
            conversation_id: conversationId,
            context: "dataset",
            error_message: error instanceof Error ? error.message : "Unknown error",
          });
        }

        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          content: "",
          kind: "model_chat",
          timestamp: new Date(),
          error: "Unable to send message. Please try again.",
        };
        setMessages(conversationId, [...localMessages, errorMessage]);
      } finally {
        // Clean up
        setActiveStreams((prev) => {
          const next = new Map(prev);
          next.delete(conversationId);
          return next;
        });
        updateConversation(conversationId, (existing) =>
          existing ? { ...existing, isStreaming: false } : existing!
        );
      }
    },
    [getMessages, setMessages, markMessagesAsConfirmed, updateConversation]
  );

  const cancelStream = useCallback(
    async (api: AssistantApi, conversationId: string) => {
      const controller = activeStreams.get(conversationId);
      if (!controller) {
        return;
      }

      // Mark as cancel requested - this flag is checked when stream ends
      controller.cancelRequested = true;

      // Tell backend to stop generating
      try {
        await api.cancelMessage(conversationId);
      } catch (error) {
        console.error("Failed to cancel message on backend:", error);
      }
    },
    [activeStreams]
  );

  // Context value updates when any getter changes (which happens when conversations state changes)
  const value = useMemo<ConversationsContextValue>(
    () => ({
      getMessages,
      getOptimisticMessageIds,
      setMessages,
      startStream,
      cancelStream,
      isStreaming,
      markMessagesAsConfirmed,
    }),
    [
      getMessages,
      getOptimisticMessageIds,
      setMessages,
      startStream,
      cancelStream,
      isStreaming,
      markMessagesAsConfirmed,
    ]
  );

  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
}

export function useConversations() {
  return useContext(ConversationsContext);
}
